import { useEffect, useMemo, useState } from "react";

import { apolloClient } from "../services/backend_gateway";
import { GetMyDataFileDownloadUrlDocument } from "../services/__generated__/graphql";

export type ExceptionType = {
  name: string;
  label: string;
  description: string;
  status_scope: string | null;
  tags: string[];
};

/**
 * Default surfaced in the /shipments Alerts dropdown when no engine-emitted
 * sidecar is available for the current run. Keeps the UI functional on:
 *   - legacy runs that pre-date the `ui_supply_walk_exception_types.json`
 *     sidecar (CHO-552 Phase 1);
 *   - non-ADMIN sessions where `getMyDataFileDownloadUrl` returns null
 *     (the resolver is currently ROOT/ADMIN-gated);
 *   - network / parse failures of any kind.
 */
const FALLBACK: ExceptionType[] = [
  {
    name: "low_inventory",
    label: "Low inventory",
    description: "",
    status_scope: "Forecast",
    tags: [],
  },
];

type ParsedRunSettings = {
  input_id: string;
  organization_id: string;
  run_attempt_id: string;
  settings_name_and_version: string;
  workflow_id: string;
};

type UseExceptionTypesReturn = {
  /** Currently displayable exception types; never empty (FALLBACK on miss). */
  data: ExceptionType[];
  /** True while the run-scoped fetch is in flight. */
  loading: boolean;
};

/**
 * Fetches the run-scoped `ui_supply_walk_exception_types.json` sidecar
 * emitted by `needs_ram_ui_supply_walk_exceptions` and exposes the list
 * of registered exception prototypes. Drives the /shipments Alerts
 * dropdown options dynamically.
 *
 * Adding a new prototype in
 * `steps/src/workflows/atomic_pivots/exception_definitions.py` and
 * re-running the engine surfaces the new alert option here without any
 * frontend code change.
 *
 * Memoized on the parsed runSettings so re-renders within the same run
 * skip the network call. Apollo additionally caches the presigned-URL
 * response keyed on the input variables.
 */
export const useExceptionTypes = (
  runSettingsJson: string,
): UseExceptionTypesReturn => {
  const runSettings = useMemo<ParsedRunSettings | null>(() => {
    if (!runSettingsJson) return null;
    try {
      return JSON.parse(runSettingsJson) as ParsedRunSettings;
    } catch {
      return null;
    }
  }, [runSettingsJson]);

  const [data, setData] = useState<ExceptionType[]>(FALLBACK);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!runSettings) {
      setData(FALLBACK);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const urlRes = await apolloClient.query({
          query: GetMyDataFileDownloadUrlDocument,
          variables: {
            runSettings: {
              inputId: runSettings.input_id,
              workflowId: runSettings.workflow_id,
              settingsNameAndVersion: runSettings.settings_name_and_version,
              runAttemptId: runSettings.run_attempt_id,
            },
            dataName: "ui_supply_walk_exception_types",
            fileFormat: "json",
          },
          fetchPolicy: "cache-first",
        });

        const url = urlRes.data?.getMyDataFileDownloadUrl;
        if (!url) {
          if (!cancelled) setData(FALLBACK);
          return;
        }

        const res = await fetch(url);
        if (!res.ok) {
          if (!cancelled) setData(FALLBACK);
          return;
        }
        const payload = (await res.json()) as { exception_types?: ExceptionType[] };
        if (cancelled) return;

        if (Array.isArray(payload.exception_types) && payload.exception_types.length > 0) {
          setData(payload.exception_types);
        } else {
          setData(FALLBACK);
        }
      } catch (err) {
        console.warn("[useExceptionTypes] fetch failed; using fallback", err);
        if (!cancelled) setData(FALLBACK);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [runSettings]);

  return { data, loading };
};
